import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';

/**
 * F8 — Şifre sıfırlama (e-posta / SMS kanal seçimi).
 *
 * Founder 2026-06-12: iki kanal da her zaman sunulur, hesap/telefon kayıtlı mı
 * KONTROL EDİLMEZ. Backend her zaman generic 204 döner (enumeration-safe); SMS
 * seçilip doğrulanmış telefon yoksa sessiz no-op. UI hiçbir hesap durumu probe etmez.
 */
type Channel = 'EMAIL' | 'SMS';
type Step = 'request' | 'confirm';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [channel, setChannel] = useState<Channel>('EMAIL');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    if (!/.+@.+\..+/.test(email)) {
      toast.error(t('forgot.toast.invalidEmail'));
      return;
    }
    setSubmitting(true);
    try {
      // Backend daima 204 (enumeration-safe). Hesap/telefon durumu sızdırılmaz.
      await api.post('/auth/password/forgot', { email: email.trim().toLowerCase(), channel });
      toast.success(t('forgot.toast.codeSent'));
      setStep('confirm');
    } catch {
      // Hata olsa bile generic davran (enumeration-safe) — yine confirm adımına geç.
      setStep('confirm');
    } finally {
      setSubmitting(false);
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      toast.error(t('forgot.toast.codeLength'));
      return;
    }
    if (newPassword.length < 12) {
      toast.error(t('forgot.toast.passwordLength'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/password/reset', { email: email.trim().toLowerCase(), code, newPassword });
      toast.success(t('forgot.toast.updated'));
      navigate('/giris');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('forgot.toast.fail'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">{t('forgot.title')}</h1>

        {step === 'request' ? (
          <>
            <p className="mb-6 text-sm text-custel-text-secondary">{t('forgot.requestSubtitle')}</p>
            <form className="space-y-4" onSubmit={onRequest}>
              <div>
                <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="email">
                  {t('forgot.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  className="input-base"
                  placeholder="ornek@custel.co"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <fieldset>
                <legend className="mb-1 block text-sm font-medium text-custel-text">{t('forgot.channelLegend')}</legend>
                <div className="flex gap-4 text-sm text-custel-text">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="channel"
                      checked={channel === 'EMAIL'}
                      onChange={() => setChannel('EMAIL')}
                    />
                    {t('forgot.channelEmail')}
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="channel" checked={channel === 'SMS'} onChange={() => setChannel('SMS')} />
                    {t('forgot.channelSms')}
                  </label>
                </div>
                <p className="mt-1 text-xs text-custel-text-tertiary">{t('forgot.channelHint')}</p>
              </fieldset>

              <button type="submit" className="btn-primary w-full" disabled={submitting}>
                {submitting ? t('forgot.sending') : t('forgot.send')}
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="mb-6 text-sm text-custel-text-secondary">{t('forgot.confirmSubtitle')}</p>
            <form className="space-y-4" onSubmit={onConfirm}>
              <div>
                <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="code">
                  {t('forgot.code')}
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="input-base text-center font-mono text-2xl tracking-widest"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="newPassword">
                  {t('forgot.newPassword')}
                </label>
                <input
                  id="newPassword"
                  type="password"
                  className="input-base"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <button type="submit" className="btn-primary w-full" disabled={submitting}>
                {submitting ? t('forgot.updating') : t('forgot.update')}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-sm">
          <Link to="/giris" className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline">
            {t('forgot.backToLogin')}
          </Link>
        </div>
      </div>
    </main>
  );
}
